// src/routes/tasks/TasksPage.tsx
import React from 'react';
import { 
  Container, 
  Title, 
  Paper, 
  Group, 
  Stack,
  Card,
  Text,
  Badge,
  Avatar,
  Button,
  SimpleGrid
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface Task {
  id: number;
  title: string;
  description: string;
  status: number;
  priority: number;
  assignedToUserId?: number;
  dueDate?: string;
}

const statusColumns = [
  { status: 0, title: 'Da Fare', color: 'gray' },
  { status: 1, title: 'In Corso', color: 'blue' },
  { status: 2, title: 'In Review', color: 'yellow' },
  { status: 3, title: 'Completato', color: 'green' },
  { status: 4, title: 'Bloccato', color: 'red' }
];

export function TasksPage() {
  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const token = localStorage.getItem('okta-token-storage');
      const { data } = await axios.get(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'}/api/ProjectTask`, {
        headers: {
          Authorization: `Bearer ${token ? JSON.parse(token).accessToken.accessToken : ''}`
        }
      });
      return data;
    }
  });

  const getPriorityColor = (priority: number) => {
    const colors: { [key: number]: string } = {
      0: 'blue',
      1: 'yellow',
      2: 'orange',
      3: 'red'
    };
    return colors[priority] || 'gray';
  };

  const getTasksByStatus = (status: number) => {
    return tasks?.filter((task: Task) => task.status === status) || [];
  };

  if (isLoading) {
    return (
      <Container size="xl" py="lg">
        <Text>Caricamento...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Kanban Board - Tasks</Title>
          <Button leftSection={<IconPlus size={16} />}>
            Nuovo Task
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="md">
          {statusColumns.map(column => (
            <Paper key={column.status} withBorder p="md" style={{ minHeight: 400 }}>
              <Group justify="space-between" mb="md">
                <Text fw={600}>{column.title}</Text>
                <Badge color={column.color} style={{ color: 'white' }}>{getTasksByStatus(column.status).length}</Badge>
              </Group>
              
              <Stack gap="sm">
                {getTasksByStatus(column.status).map((task: Task) => (
                  <Card key={task.id} withBorder padding="sm" style={{ cursor: 'pointer' }}>
                    <Stack gap="xs">
                      <Text fw={500} size="sm">{task.title}</Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {task.description}
                      </Text>
                      
                      <Group justify="space-between" mt="xs">
                        <Badge size="xs" color={getPriorityColor(task.priority)} style={{ color: 'white' }}>
                          P{task.priority}
                        </Badge>
                        {task.assignedToUserId && (
                          <Avatar size="xs" radius="xl" color="blue">
                            {task.assignedToUserId}
                          </Avatar>
                        )}
                      </Group>
                      
                      {task.dueDate && (
                        <Text size="xs" c="dimmed">
                          Scadenza: {new Date(task.dueDate).toLocaleDateString()}
                        </Text>
                      )}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
